import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatCard,
  StatusBadge,
  UIButton,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import { buildTriageQueue, triageBucketLabel } from './utils/triageQueue';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const severityOptions = ['all', 'critical', 'high', 'medium', 'low'];
const sourceOptions = ['all', 'incident', 'ticket', 'site', 'customer'];
const queueFilters = ['all', 'critical_now', 'needs_attention', 'active_handling', 'watch'];

const formatLastUpdate = (item) => {
  if (!item?.lastUpdate?.at) return 'Belum ada tindakan tercatat';
  const label = getExecutionActionLabel(item.lastUpdate.label);
  const at = new Date(item.lastUpdate.at);
  return `${label} | ${Number.isNaN(at.getTime()) ? '-' : at.toLocaleString('id-ID')}`;
};

export default function AdminTriageQueue() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    refresh,
    incidents,
    tickets,
    customers,
    odps,
    olts,
    nasRouters,
    sites,
    lastUpdated,
  } = useOperationalDataset(navigate);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [queueFilter, setQueueFilter] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadExecutionLogs = async () => {
      setExecutionLoading(true);
      setExecutionError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: { limit: 150 },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (fetchError) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionError(String(fetchError?.message || 'Gagal memuat execution log.'));
      } finally {
        if (!cancelled) setExecutionLoading(false);
      }
    };
    loadExecutionLogs();
    return () => {
      cancelled = true;
    };
  }, [navigate, lastUpdated]);

  const triage = useMemo(() => buildTriageQueue({
    incidents,
    tickets,
    customers,
    odps,
    olts,
    nasRouters,
    sites,
    executionLogs,
  }), [incidents, tickets, customers, odps, olts, nasRouters, sites, executionLogs]);

  const filteredItems = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    return triage.items.filter((item) => {
      if (sourceFilter !== 'all' && item.sourceType !== sourceFilter) return false;
      if (severityFilter !== 'all' && String(item.severity || '').toLowerCase() !== severityFilter) return false;
      if (queueFilter !== 'all' && item.bucket !== queueFilter) return false;
      if (!needle) return true;
      const hay = `${item.title} ${item.subtitle} ${item.impactedSummary} ${item.contextSummary}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [triage.items, sourceFilter, severityFilter, queueFilter, query]);

  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedItemId('');
      return;
    }
    const current = filteredItems.find((item) => item.id === selectedItemId);
    if (!current) setSelectedItemId(filteredItems[0].id);
  }, [filteredItems, selectedItemId]);

  const selectedItem = useMemo(
    () => filteredItems.find((item) => item.id === selectedItemId) || null,
    [filteredItems, selectedItemId],
  );

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/triage" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Command Center"
          title="Triage Queue"
          subtitle="Queue prioritas tindakan operasional. Berbeda dari Operations Overview yang fokus scan kondisi global, halaman ini fokus memilih item yang harus ditangani lebih dulu."
          action={(
            <UIButton variant="ghost" onClick={refresh} disabled={loading || executionLoading}>
              <RefreshCw className={`h-4 w-4 ${(loading || executionLoading) ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        {executionError ? (
          <SectionContainer className="mb-4 border border-amber-500/30 bg-amber-500/10 text-sm text-amber-100">
            Menampilkan data yang tersedia. Execution log belum penuh: {executionError}
          </SectionContainer>
        ) : null}

        <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Critical Now" value={triage.summary.criticalNow} note="Item paling mendesak" tone={triage.summary.criticalNow > 0 ? 'danger' : 'ok'} />
          <StatCard title="Active Handling" value={triage.summary.activeHandling} note="Sedang ditangani aktif" tone={triage.summary.activeHandling > 0 ? 'info' : 'neutral'} />
          <StatCard title="High Impact" value={triage.summary.highImpact} note="Dampak besar pada customer/site" tone={triage.summary.highImpact > 0 ? 'warn' : 'neutral'} />
          <StatCard title="No Recent Update" value={triage.summary.noRecentUpdate} note="Butuh follow-up operasional" tone={triage.summary.noRecentUpdate > 0 ? 'warn' : 'ok'} />
        </div>

        <SectionContainer className="mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {queueFilters.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setQueueFilter(item)}
                className={`rounded-md border px-2.5 py-1 text-xs ${
                  queueFilter === item
                    ? 'border-cyan-400/60 bg-cyan-500/20 text-cyan-100'
                    : 'border-white/10 bg-[#0a1322] text-slate-300 hover:border-cyan-400/30'
                }`}
              >
                {item === 'all' ? 'All' : triageBucketLabel(item)}
              </button>
            ))}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <select
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
                className="rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-1 text-xs text-slate-200"
              >
                {sourceOptions.map((item) => <option key={item} value={item}>{item === 'all' ? 'All Source' : item}</option>)}
              </select>
              <select
                value={severityFilter}
                onChange={(event) => setSeverityFilter(event.target.value)}
                className="rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-1 text-xs text-slate-200"
              >
                {severityOptions.map((item) => <option key={item} value={item}>{item === 'all' ? 'All Severity' : item}</option>)}
              </select>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Cari context triage..."
                className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-1 text-xs text-slate-100 placeholder:text-slate-500"
              />
              <UIButton as="link" to="/operations" variant="ghost">
                Open in Operations
              </UIButton>
            </div>
          </div>
        </SectionContainer>

        <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
          <SectionContainer>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Priority Queue</p>
              <span className="text-[11px] text-slate-500">{filteredItems.length} items</span>
            </div>
            {loading || executionLoading ? (
              <p className="text-sm text-slate-400">Masih memuat data...</p>
            ) : null}
            {!loading && !executionLoading && filteredItems.length === 0 ? (
              <p className="text-sm text-slate-400">
                {triage.items.length === 0
                  ? 'Belum ada item yang perlu ditriase sekarang. Belum ada incident atau ticket aktif.'
                  : 'Tidak ada item triage yang cocok dengan filter.'}
              </p>
            ) : null}
            {!loading && !executionLoading && filteredItems.length > 0 ? (
              <div className="space-y-2">
                {filteredItems.slice(0, 12).map((item) => {
                  const active = item.id === selectedItemId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedItemId(item.id)}
                      className={`w-full rounded-md border px-3 py-2 text-left transition ${
                        active
                          ? 'border-cyan-400/60 bg-cyan-500/10 ring-1 ring-cyan-400/30'
                          : 'border-white/10 bg-[#0a1322] hover:border-cyan-400/30'
                      }`}
                    >
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-1.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge tone={item.tone}>{String(item.severity || 'low').toUpperCase()}</StatusBadge>
                          <StatusBadge tone="neutral">{item.sourceType}</StatusBadge>
                          <StatusBadge tone={item.bucket === 'critical_now' ? 'danger' : item.bucket === 'needs_attention' ? 'warn' : item.bucket === 'active_handling' ? 'info' : 'neutral'}>
                            {triageBucketLabel(item.bucket)}
                          </StatusBadge>
                        </div>
                        <span className="text-[10px] text-slate-500">Score {item.impactScore}</span>
                      </div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-[11px] text-slate-300">{item.subtitle}</p>
                      <p className="mt-1 text-[11px] text-slate-400">{item.impactedSummary}</p>
                      <p className="text-[11px] text-slate-500">{item.contextSummary}</p>
                      {Array.isArray(item.reasons) && item.reasons.length > 0 ? (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {item.reasons.map((reason) => (
                            <StatusBadge key={`${item.id}-${reason}`} tone="neutral">{reason}</StatusBadge>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-[10px] text-slate-500">Context operasional belum lengkap.</p>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </SectionContainer>

          <SectionContainer className="space-y-3">
            {!selectedItem ? (
              <p className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">
                Pilih item triage untuk melihat context detail dan aksi cepat.
              </p>
            ) : (
              <>
                <div className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-2">
                  <p className="mb-1 text-xs uppercase tracking-[0.12em] text-slate-400">Why this is here</p>
                  {Array.isArray(selectedItem.reasons) && selectedItem.reasons.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {selectedItem.reasons.map((reason) => (
                        <StatusBadge key={`selected-${reason}`} tone="neutral">{reason}</StatusBadge>
                      ))}
                    </div>
                  ) : (
                    <p className="mb-2 text-xs text-slate-500">Context operasional belum lengkap.</p>
                  )}
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    <StatusBadge tone={selectedItem.tone}>{String(selectedItem.severity || 'low').toUpperCase()}</StatusBadge>
                    <StatusBadge tone="neutral">{selectedItem.sourceType}</StatusBadge>
                    <StatusBadge tone={selectedItem.bucket === 'critical_now' ? 'danger' : selectedItem.bucket === 'needs_attention' ? 'warn' : selectedItem.bucket === 'active_handling' ? 'info' : 'neutral'}>
                      {triageBucketLabel(selectedItem.bucket)}
                    </StatusBadge>
                  </div>
                  <p className="text-sm font-semibold text-white">{selectedItem.title}</p>
                  <p className="text-xs text-slate-300">{selectedItem.subtitle}</p>
                  <p className="mt-1 text-xs text-slate-400">{selectedItem.impactedSummary}</p>
                  <p className="text-xs text-slate-500">{selectedItem.contextSummary}</p>
                </div>

                <div className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-2">
                  <p className="mb-1 text-xs uppercase tracking-[0.12em] text-slate-400">Last Operational Update</p>
                  <p className="text-xs text-slate-300">{formatLastUpdate(selectedItem)}</p>
                  {selectedItem.lastUpdate?.message ? <p className="mt-1 text-[11px] text-slate-400">{selectedItem.lastUpdate.message}</p> : null}
                  {selectedItem.lastUpdate?.actor ? <p className="text-[11px] text-slate-500">Actor: {selectedItem.lastUpdate.actor}</p> : null}
                </div>

                <div className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-2">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Action Shortcuts</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(selectedItem.shortcuts || []).slice(0, 1).map((shortcut) => (
                      <UIButton key={shortcut.to} as="link" to={shortcut.to} variant="subtle">
                        {shortcut.label}
                      </UIButton>
                    ))}
                    {selectedItem.ctas.slice(0, 2).map((cta) => (
                      <UIButton key={cta.to} as="link" to={cta.to} variant="ghost">
                        <ArrowUpRight className="h-4 w-4" />
                        {cta.label}
                      </UIButton>
                    ))}
                    {selectedItem.sourceType !== 'ticket' ? (
                      <UIButton as="link" to="/tickets" variant="ghost">Open in Tickets</UIButton>
                    ) : null}
                    {selectedItem.sourceType !== 'incident' ? (
                      <UIButton as="link" to="/recovery" variant="ghost">Open in Recovery</UIButton>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-md border border-white/10 bg-[#0a1322] px-3 py-2">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Context References</p>
                  <div className="space-y-1 text-xs text-slate-300">
                    <p>Incident: <span className="text-white">{selectedItem.references.incidentId || '-'}</span></p>
                    <p>Ticket: <span className="text-white">{selectedItem.references.ticketId || '-'}</span></p>
                    <p>Customer: <span className="text-white">{selectedItem.references.customerId || '-'}</span></p>
                    <p>Site: <span className="text-white">{selectedItem.references.siteId || '-'}</span></p>
                    <p>Router: <span className="text-white">{selectedItem.references.routerId || '-'}</span></p>
                  </div>
                </div>
              </>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
    </AppShell>
  );
}
